import { useState, useCallback } from 'react';
import { getWorkflowAIService } from '../services/workflowAIService';
import { validateWorkflowV2 } from '../schemas/workflowSchema';

/**
 * Hook for managing AI chat state for workflow generation/modification.
 *
 * @param {Object} options
 * @param {Function} options.onApply - Called when user applies AI-suggested changes (receives v2 schema)
 * @param {Object} options.currentSchema - Current workflow v2 schema (for modifications)
 * @returns {Object} - { messages, isLoading, pendingSchema, sendMessage, applyChanges, revertChanges, clearChat }
 */
export function useWorkflowChat({ onApply, currentSchema } = {}) {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingSchema, setPendingSchema] = useState(null);
  const [pendingSummary, setPendingSummary] = useState('');
  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isLoading) return;

    // Add user message
    const userMsg = { id: `msg-${Date.now()}`, role: 'user', content: text, timestamp: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const aiService = getWorkflowAIService();

      if (currentSchema) {
        // Modify existing workflow
        const { schema, summary } = await aiService.modifyWorkflow(text, currentSchema);

        // Validate the returned schema
        const validation = validateWorkflowV2(schema);
        if (!validation.success) {
          const errorMsg = {
            id: `msg-${Date.now() + 1}`,
            role: 'assistant',
            content: `I generated a response but it had validation errors. Let me try again.\n\nErrors: ${validation.error.issues.map((i) => i.message).join(', ')}`,
            timestamp: new Date(),
            isError: true,
          };
          setMessages((prev) => [...prev, errorMsg]);
          return;
        }

        setPendingSchema(validation.data);
        setPendingSummary(summary);

        const aiMsg = {
          id: `msg-${Date.now() + 1}`,
          role: 'assistant',
          content: summary,
          timestamp: new Date(),
          hasPendingChanges: true,
        };
        setMessages((prev) => [...prev, aiMsg]);
      } else {
        // Generate new workflow — this path is used from CreateWorkflowDialog
        const aiMsg = {
          id: `msg-${Date.now() + 1}`,
          role: 'assistant',
          content: 'Generating your workflow...',
          timestamp: new Date(),
          isGenerating: true,
        };
        setMessages((prev) => [...prev, aiMsg]);
      }
    } catch (err) {
      const errorMsg = {
        id: `msg-${Date.now() + 1}`,
        role: 'assistant',
        content: `Something went wrong: ${err.message || 'Unknown error'}. Please try again.`,
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, currentSchema]);

  const applyChanges = useCallback(() => {
    if (pendingSchema && onApply) {
      onApply(pendingSchema);
      // Mark the last assistant message as applied
      setMessages((prev) => prev.map((m) =>
        m.hasPendingChanges ? { ...m, hasPendingChanges: false, applied: true } : m
      ));
      setPendingSchema(null);
      setPendingSummary('');
    }
  }, [pendingSchema, onApply]);

  const revertChanges = useCallback(() => {
    setPendingSchema(null);
    setPendingSummary('');
    // Mark the last assistant message as reverted
    setMessages((prev) => prev.map((m) =>
      m.hasPendingChanges ? { ...m, hasPendingChanges: false, reverted: true } : m
    ));
    const revertMsg = {
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: 'Changes reverted. How else can I help?',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, revertMsg]);
  }, []);

  const clearChat = useCallback(() => {
    setMessages([]);
    setPendingSchema(null);
    setPendingSummary('');
  }, []);

  return {
    messages,
    isLoading,
    pendingSchema,
    pendingSummary,
    sendMessage,
    applyChanges,
    revertChanges,
    clearChat,
  };
}

export default useWorkflowChat;
